/**
 * A person's own named times — the owner's request of 4 October 2026.
 *
 * Besides Breakfast, Lunch, Dinner and Bedtime, a person can add up to six
 * times of their own day — "Evening tea, 5:00 PM", "After walk, 7:15 AM" —
 * and take a medicine before, with or after one exactly as after a meal. The
 * four meals stay as they are: prescription notation (1-0-1, "after food")
 * is written about them, and a custom time is never in a notation.
 *
 * The name is the person's own words: never translated, shown as typed.
 * An anchor refers to a time by key, `time:<id>`, so renaming one moves
 * nothing and changing its clock time moves every dose tied to it, as a
 * meal time does.
 *
 * Pure: shared by the API, which enforces it, and the phone and the
 * websites, which say it before anybody presses Save.
 */
import type { AnchorKey, AnchorTimes, LocalTime, MealAnchor, MealKey, MealTimes, RoutineTimeKey } from './types.js';
import { MEAL_KEYS } from './types.js';
import { FOOD_OFFSET_MINUTES } from './notation.js';
import type { FoodRelation } from './types.js';

/** How many own times a person may have. More is a schedule, not a routine. */
export const MAX_ROUTINE_TIMES = 6;
/** Characters in a name, after trimming. */
export const ROUTINE_TIME_NAME_MAX = 30;

export interface RoutineTime {
  id: string;
  /** As typed. */
  name: string;
  /** `HH:MM`, in the person's own zone. */
  time: LocalTime;
}

export const routineTimeKey = (id: string): RoutineTimeKey => `time:${id}`;

export function isRoutineTimeKey(key: string): key is RoutineTimeKey {
  return key.startsWith('time:') && key.length > 5;
}

export function isMealKey(key: string): key is MealKey {
  return (MEAL_KEYS as readonly string[]).includes(key);
}

/** The id inside `time:<id>`, or null for a meal. */
export function routineTimeId(key: string): string | null {
  return isRoutineTimeKey(key) ? key.slice(5) : null;
}

/** The meals and the person's own times, as one lookup for materialize. */
export function anchorTimes(meals: MealTimes, own: readonly Pick<RoutineTime, 'id' | 'time'>[] = []): AnchorTimes {
  const out: AnchorTimes = { ...meals };
  for (const t of own) out[routineTimeKey(t.id)] = t.time;
  return out;
}

/**
 * A name as it will be stored: trimmed, with runs of spaces made one. Null
 * when it is not a name — empty, too long, a control character — or when
 * another of this person's times already has it (case does not tell two
 * apart: "Tea" and "tea" would read as the same time on a reminder).
 */
export type NameProblem = 'empty' | 'too_long' | 'taken';

export function cleanRoutineTimeName(
  raw: string,
  others: readonly Pick<RoutineTime, 'id' | 'name'>[] = [],
  selfId: string | null = null,
): { ok: true; name: string } | { ok: false; problem: NameProblem } {
  // eslint-disable-next-line no-control-regex
  const name = raw.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();
  if (name.length === 0) return { ok: false, problem: 'empty' };
  if ([...name].length > ROUTINE_TIME_NAME_MAX) return { ok: false, problem: 'too_long' };
  const lower = name.toLocaleLowerCase();
  if (others.some((o) => o.id !== selfId && o.name.toLocaleLowerCase() === lower)) return { ok: false, problem: 'taken' };
  return { ok: true, name };
}

/**
 * The anchors for a medicine taken at some of a person's own times — the
 * same offsets the meals use (`FOOD_OFFSET_MINUTES`: before is 30 minutes
 * before, after 30 minutes after), so "after Evening tea" means what "after
 * lunch" means.
 */
export function routineAnchors(
  picks: Readonly<Record<string, number>>,
  food: FoodRelation,
): MealAnchor[] {
  const offset = FOOD_OFFSET_MINUTES[food];
  return Object.entries(picks)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => ({ meal: routineTimeKey(id), offsetMinutes: offset, ...(qty !== 1 ? { doseQuantity: qty } : {}) }));
}

/** `HH:MM` moved by `minutes`, round the clock. */
export function shiftLocalTime(time: LocalTime, minutes: number): LocalTime {
  const [h, m] = time.split(':').map(Number) as [number, number];
  const total = ((h * 60 + m + minutes) % 1440 + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * Deleting a time some medicines are tied to: what becomes of their anchors.
 *
 * Never silently dropped. Either they **move** to another time (a meal or
 * another of the person's own), keeping their offset — "after Evening tea"
 * becomes "after Dinner" — or they are **kept** at the clock time they ring
 * at now, every day, as fixed times. Anchors tied to anything else are left
 * as they are.
 */
export interface Replanned {
  /** What stays meal-relative (moved anchors included). Empty: none. */
  anchors: MealAnchor[];
  /** What becomes a fixed clock time, grouped by dose quantity (a fixed schedule has one). */
  fixed: Array<{ times: LocalTime[]; doseQuantity: number | undefined }>;
}

export function replanAnchors(
  anchors: readonly MealAnchor[],
  removed: RoutineTimeKey,
  removedTime: LocalTime,
  choice: { kind: 'move'; to: AnchorKey } | { kind: 'keep' },
): Replanned {
  const keep = anchors.filter((a) => a.meal !== removed);
  const tied = anchors.filter((a) => a.meal === removed);
  if (choice.kind === 'move') {
    return { anchors: [...keep, ...tied.map((a) => ({ ...a, meal: choice.to }))], fixed: [] };
  }
  const byQty = new Map<string, { times: LocalTime[]; doseQuantity: number | undefined }>();
  for (const a of tied) {
    const k = String(a.doseQuantity ?? '');
    const group = byQty.get(k) ?? { times: [], doseQuantity: a.doseQuantity };
    const time = shiftLocalTime(removedTime, a.offsetMinutes);
    if (!group.times.includes(time)) group.times.push(time);
    byQty.set(k, group);
  }
  return { anchors: keep, fixed: [...byQty.values()].map((g) => ({ ...g, times: g.times.sort() })) };
}
